import React, { RefObject } from "react";
import StepModel from "../../../model/configurator/StepModel";
import Field from "../../../model/configurator/Field";
import BaseStep from "../../shared/steps/BaseStep";
import TwsInputMethodField from "./custom-fields/tws-input-method/TwsInputMethodField";
import formModelStore from "../../../store/FormModelStore";
import TwsInstallationTypeField from "./custom-fields/tws-installation-type/TwsInstallationTypeField";
import APIServicesRegistry from "../../../api-services/APIServicesRegistry";
import TWSCalculationApiService from "../../../api-services/TWSCalculationApiService";
import flattenConfigurationModel from "../../../flattenConfigurationModel";
import TwsMatchingUnitField from "./custom-fields/tws-matching-units/TwsMatchingUnitField";
import { Calculation } from "../../../../src/application/Contracts";
import { InstallationTypeInputMethod } from "./input-methods/InstallationTypeInputMethod";
import MatchingUnitModel = Calculation.MatchingUnitModel;
import InputMethod = Calculation.InputMethod;
import InstallationType = Calculation.InstallationType;
import { CapacityPInputMethod } from "./input-methods/CapacityPInputMethod";
import BaseInputMethodCalculationComponentInterface from "./input-methods/BaseInputMethodCalculationComponentInterface";
import { ProductCatalogInputMethod } from "./input-methods/ProductCatalogInputMethod";
import PrimaryTankCalculationError = Calculation.PrimaryTankCalculationError;
import { TWSInputMethodFields } from "./Interfaces";
import TwsProductCatalogSelectProductField
    from "./custom-fields/tws-product-catalog-select-product/TwsProductCatalogSelectProductField";
import { StepsSlugs } from "./TWSProjectCalculationContainer";
import { ShowTankSelectionStep, ShowWaterSystemSelectionStep } from "../../utils/FormModelStoreTWSHelper";


interface SelfState {
    matchingUnitModels: MatchingUnitModel[];
    equivalentInStandardApartments: number;
    selectedInstallationType: InstallationType;
    primaryTankCalculationError?: PrimaryTankCalculationError;
    primaryTankUnitCapacity: number;
    primaryTankStorage: number;
    primaryTankMadeOf: string;
    primaryTankFlowRate: number;
    selectedInputMethod: InputMethod,
    editModeLoaded: boolean,
    availableTankVolumes: number[],
}

export class InputMethodStep extends BaseStep {

    _tWSCalculationApiService: TWSCalculationApiService | null = null;
    state: SelfState = {
        matchingUnitModels: [],
        equivalentInStandardApartments: 0,
        selectedInstallationType: InstallationType.ApartmentsBlocks,
        primaryTankUnitCapacity: 0,
        primaryTankStorage: 0,
        primaryTankMadeOf: '',
        primaryTankFlowRate: 0,
        selectedInputMethod: InputMethod.InstallationType,
        editModeLoaded: false,
        availableTankVolumes: [],
    }

    installationTypeInputRef: RefObject<InstallationTypeInputMethod> = React.createRef();
    capacityInputRef: RefObject<CapacityPInputMethod> = React.createRef();
    productCatalogInputRef: RefObject<ProductCatalogInputMethod> = React.createRef();

    constructor(props: any) {
        super(props);

        window.scheduleAtDocumentReady(() => {
            this._tWSCalculationApiService = APIServicesRegistry.getService(TWSCalculationApiService);

            this._tWSCalculationApiService?.getAvailableTankVolumesForCapacityMethod({ primaryTank: this.fields.capacityPrimaryTank.value })
                .then((response: any): void => this.setState({ availableTankVolumes: response.data }));
        })
    }

    protected override onEditMode(): void {
        if (this.state.editModeLoaded)
            return;

        super.onEditMode();

        this.setState({
            equivalentInStandardApartments: this.props.model.inputMethod.installationTypeCalculation?.equivalentInStandardApartments,
            selectedInputMethod: this.props.model.inputMethod.inputMethod.value,
            selectedInstallationType: this.props.model.inputMethod.installationType.value,
            primaryTankUnitCapacity: this.props.model.inputMethod.results?.value.primaryTankUnitCapacity,
            primaryTankStorage: this.props.model.inputMethod.results?.value.primaryTankStorage,
            primaryTankMadeOf: this.props.model.inputMethod.results?.value.primaryTankMadeOf,
            primaryTankFlowRate: this.props.model.inputMethod.results?.value.primaryTankFlowRate,
            matchingUnitModels: this.props.model.inputMethod.results?.value.matchingUnitModels,
            primaryTankCalculationError: null,
            editModeLoaded: true //Input method step is in edit mode...
        });

        window.scheduleAtDocumentReady(() => {
            this.props.updateStepAvailability(StepsSlugs.TankSelection, ShowTankSelectionStep());
            this.props.updateStepAvailability(StepsSlugs.WaterSystem, ShowWaterSystemSelectionStep());

            this.props.stepChangeCallback(this.props.stepSlug, this.getActiveInputMethod()?.canProceed() ?? false);
        });
    }

    static getStepModel(model: any): StepModel | null {
        return new StepModel('inputMethod', model.inputMethod)
    }

    static getFields(step: StepModel) {
        const createField = (name: string, required: boolean = false) => new Field(name, step, true, formModelStore, required)
        return {
            inputMethod: new TwsInputMethodField("inputMethod", step, true, formModelStore),
            installationType: new TwsInstallationTypeField("installationType", step, true, formModelStore),
            maxChargingTimeForAccumulation: createField("installationTypeCalculation.maxChargingTimeForAccumulation"),
            maxChargingTimeForAccumulationRestaurants: createField("installationTypeCalculation.maxChargingTimeForAccumulation"),
            maxChargingTimeForAccumulationSportCenters: createField("installationTypeCalculation.maxChargingTimeForAccumulation"),
            tDhw: createField("installationTypeCalculation.tDhw", true),
            park: createField("installationTypeCalculation.park"),
            standing: createField("installationTypeCalculation.standing"),
            situation: createField("installationTypeCalculation.situation"),
            apartmentsStudio: createField("installationTypeCalculation.apartmentsStudio"),
            apartmentsT1Room: createField("installationTypeCalculation.apartmentsT1Room"),
            apartmentsT2Room: createField("installationTypeCalculation.apartmentsT2Room"),
            apartmentsT3Room: createField("installationTypeCalculation.apartmentsT3Room"),
            apartmentsT4Room: createField("installationTypeCalculation.apartmentsT4Room"),
            apartmentsT5Room: createField("installationTypeCalculation.apartmentsT5Room"),
            apartmentsT6Room: createField("installationTypeCalculation.apartmentsT6Room"),
            primaryTankChargingPtDt: createField("installationTypeCalculation.primaryTankChargingPtDt"),
            primaryTankAquaEfficiency: createField("installationTypeCalculation.primaryTankAquaEfficiency"),
            primaryTankAvailableCapacity: createField("installationTypeCalculation.primaryTankAvailableCapacity"),
            madeOf: createField("installationTypeCalculation.madeOf"),
            primaryStorage: createField("installationTypeCalculation.primaryStorage"),
            unitCapacity: createField("installationTypeCalculation.unitCapacity"),
            matchingUnit: new TwsMatchingUnitField("installationTypeCalculation.selectedUnitModel", step, true, formModelStore),
            numberOfBeds: createField("installationTypeCalculation.numberOfBeds", true),
            numberOfShowerRooms: createField("installationTypeCalculation.numberOfShowerRooms", true),
            numberOfShowersPerShowerRoom: createField("installationTypeCalculation.numberOfShowersPerShowerRoom", true),
            showerConsumption: createField("installationTypeCalculation.showerConsumption", true),
            showerMeanDuration: createField("installationTypeCalculation.showerMeanDuration", true),
            numberMealsBreakfast: createField("installationTypeCalculation.numberMealsBreakfast", true),
            numberMealsLunch: createField("installationTypeCalculation.numberMealsLunch", true),
            numberMealsDinner: createField("installationTypeCalculation.numberMealsDinner", true),
            restaurantCategory: createField("installationTypeCalculation.restaurantCategory"),
            numberOfRooms: createField("installationTypeCalculation.numberOfRooms", true),
            hotelStanding: createField("installationTypeCalculation.hotelStanding"),
            mountain: createField("installationTypeCalculation.mountain"),
            bedsPerRoom: createField("installationTypeCalculation.bedsPerRoom", true),
            campingClassification: createField("installationTypeCalculation.campingClassification"),
            campingSituation: createField("installationTypeCalculation.campingSituation"),
            sportCenterGymnasiumNumberOfSportHalls: createField("installationTypeCalculation.sportCenterGymnasiumNumberOfSportHalls", true),
            sportCenterStadiumNumberOfSportGrounds: createField("installationTypeCalculation.sportCenterStadiumNumberOfSportGrounds", true),
            capacityCapacity: createField("capacityCalculation.capacity", true),
            capacityVolume: createField("capacityCalculation.volume", true),
            capacityPrimaryTank: createField("capacityCalculation.primaryTank"),
            capacitySecondaryTank: createField("capacityCalculation.secondaryTank"),
            productCatalogProductFamilies: new TwsProductCatalogSelectProductField("productCatalogCalculation.availableProducts", step, true, formModelStore),
            selectedProduct: new TwsProductCatalogSelectProductField("productCatalogCalculation.selectedProduct", step, true, formModelStore),
            temperatureProgram: createField("productCatalogCalculation.temperatureProgram"),
            capacityInstantaneous: createField("capacityCalculation.instantaneous"),
            capacitySemiInstantaneous: createField("capacityCalculation.semiInstantaneous"),
            productCatalogInstantaneous: createField("productCatalogCalculation.instantaneous"),
            pushButtons: createField("installationTypeCalculation.pushButtons"),
        }
    }

    static getFirstVisibleField(fields: any): StepModel | null {
        return fields.inputMethod;
    }

    static getLastVisibleField(fields: any): StepModel | null {
        return fields.installationType;
    }

    renderInputMethod = () => {
        switch (this.state.selectedInputMethod) {
            case Calculation.InputMethod.Capacity:
                return <CapacityPInputMethod ref={this.capacityInputRef} renderField={this.renderField}
                    apiService={this._tWSCalculationApiService}
                    availableTankVolumes={this.state.availableTankVolumes}
                    fields={this.fields} />
            case Calculation.InputMethod.ProductCatalog:
                return <ProductCatalogInputMethod ref={this.productCatalogInputRef} fields={this.getFieldsAsType()}
                    renderField={this.renderField}
                    updateStepAvailability={this.props.updateStepAvailability}
                    apiService={this._tWSCalculationApiService}
                    restoredProduct={this.props.model.inputMethod.productCatalogCalculation.restoredSelectedProductData.value} />
            case Calculation.InputMethod.InstallationType:
                return <InstallationTypeInputMethod ref={this.installationTypeInputRef} renderField={this.renderField}
                    apiService={this._tWSCalculationApiService}
                    fields={this.fields} {...this.state} />
            default:
                return <div>EMPTY</div>
        }
    }

    render() {
        return (<React.Fragment>
            <div className={`generic-step-content`}>
                <div className={`step-row`}>
                    <div className={`title`}>{__("Select input method")}</div>
                    {this.renderField(this.fields.inputMethod)}
                </div>
                {this.renderInputMethod()}
            </div>
        </React.Fragment>)
    }

    async updateAvailableTankVolumes(isPrimaryTank: boolean) {
        await this._tWSCalculationApiService?.getAvailableTankVolumesForCapacityMethod({ primaryTank: isPrimaryTank })
            .then((response: any): void => this.setState({ availableTankVolumes: response.data }));

        if (!this.state.availableTankVolumes.includes(this.fields.capacityVolume.value)) {
            formModelStore.setFormModelValue(this.fields.capacityVolume.getModelPath(), this.state.availableTankVolumes[0]);
        }
    }

    runDisableLogic = async () => {
        if (formModelStore.recentlyChangedField === this.fields.capacitySemiInstantaneous.getModelPath()) {
            if (this.fields.capacitySemiInstantaneous.value === true) {
                await this.updateAvailableTankVolumes(false);
                this.fields.capacityPrimaryTank.setValue(false);
                this.fields.capacityInstantaneous.setValue(false);
            }
        }
        if (formModelStore.recentlyChangedField === this.fields.capacityPrimaryTank.getModelPath()) {
            await this.updateAvailableTankVolumes(this.fields.capacityPrimaryTank.value);

            if (!this.state.availableTankVolumes.includes(this.fields.capacityVolume.value)) {
                formModelStore.setFormModelValue(this.fields.capacityVolume.getModelPath(), this.state.availableTankVolumes[0]);
            }

            if (this.fields.capacityPrimaryTank.value === true) {
                this.fields.capacityInstantaneous.setValue(true);
                this.fields.capacitySemiInstantaneous.setValue(false);
            }
            if (this.fields.capacityInstantaneous.value === false) {
                this.fields.capacitySemiInstantaneous.setValue(true);
            }
        }
        if (formModelStore.recentlyChangedField === this.fields.capacityInstantaneous.getModelPath()) {
            if (this.fields.capacityInstantaneous.value === true) {
                this.fields.capacitySemiInstantaneous.setValue(false);
            }
        }
        if (formModelStore.recentlyChangedField === this.fields.capacitySemiInstantaneous.getModelPath()) {
            if (this.fields.capacitySemiInstantaneous.value === false) {
                this.fields.capacityInstantaneous.setValue(true);
            }
        }
    }

    onFormModelChange() {
        formModelStore.recentlyChangedFormSection = this.props.stepSlug;
        this.setState({
            selectedInstallationType: formModelStore.formModel.inputMethod.installationType.value,
            selectedInputMethod: formModelStore.formModel.inputMethod.inputMethod.value
        });
        this.runDisableLogic().then(() => {
            this.props.setLastAvailableStep(this.props.stepSlug);
            this.cancelSelectionsFromModelStore();
            this.props.stepChangeCallback(this.props.stepSlug, this.getActiveInputMethod()?.canProceed() ?? false);

            this.props.updateStepAvailability(StepsSlugs.TankSelection, ShowTankSelectionStep());
            this.props.updateStepAvailability(StepsSlugs.WaterSystem, ShowWaterSystemSelectionStep());
            setTimeout(() => {
                if (!this.validateCurrentCalculationModel()) {
                    return;
                }
                this.calculateMethod();
                this.props.stepChangeCallback(this.props.stepSlug, this.getActiveInputMethod()?.canProceed() ?? false)
                this.props.setDirty()
            }, 100);
        });
    }

    fullFillRequest = (response: any) => {
        this.setState(response.data)
        //TODO: DECIDE WHICH PLACE IS MORE APPRPPRIATE / ...THROW AWAY ONE OF THEM BEFORE KICKOFF
        formModelStore.formModel.inputMethod.results.value = {
            equivalentInStandardApartments: response.data.equivalentInStandardApartments,
            primaryTankCalculationError: response.data.primaryTankCalculationError,
            matchingUnitModels: response.data.matchingUnitModels,
            primaryTankUnitCapacity: response.data.primaryTankUnitCapacity,
            primaryTankStorage: response.data.primaryTankStorage,
            primaryTankMadeOf: response.data.primaryTankMadeOf,
            primaryTankFlowRate: response.data.primaryTankFlowRate
        };
        formModelStore.formModel.inputMethod.installationTypeCalculation.equivalentInStandardApartments = response.data.equivalentInStandardApartments;
        this.clearWaterSystems();
    }

    clearWaterSystems = () => {
        formModelStore.formModel.waterSystem.selectedTapWaterSystemModel = {}
        formModelStore.formModel.waterSystem.selectedTWSOptions = []
    }

    getActiveInputMethod = (): BaseInputMethodCalculationComponentInterface | null => {
        switch (formModelStore.formModel.inputMethod.inputMethod.value) {
            case Calculation.InputMethod.InstallationType:
                return this.installationTypeInputRef.current;
            case Calculation.InputMethod.Capacity:
                return this.capacityInputRef.current;
            case Calculation.InputMethod.ProductCatalog:
            default:
                return this.productCatalogInputRef.current;
        }
    }

    calculateMethod = () => {
        if (formModelStore.formModel.inputMethod.inputMethod.value === Calculation.InputMethod.InstallationType) {
            this._tWSCalculationApiService?.calculateMatchingUnits(flattenConfigurationModel(formModelStore).formModel)
                .then(this.fullFillRequest)
        } else {
            this.clearWaterSystems();
        }
    }

    validateCurrentCalculationModel = (): boolean | undefined => {
        return this.getActiveInputMethod()?.isValidFormModel(formModelStore.formModel.inputMethod.installationType.value)
    }

    cancelSelectionsFromModelStore = () => {
        if (formModelStore.recentlyChangedField === this.fields.matchingUnit.getModelPath()) {
            return;
        }
        if (formModelStore.recentlyChangedField === this.fields.installationType.getModelPath()) {
            this.setState({
                matchingUnitModels: [],
                primaryTankUnitCapacity: 0,
                primaryTankStorage: 0,
                primaryTankMadeOf: '',
                primaryTankCalculationError: undefined,
            })
        }
        this.props.stepChangeCallback(this.props.stepSlug, false);
        formModelStore.formModel.inputMethod.installationTypeCalculation.selectedUnitModel = {};
    }

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{}>, snapshot?: any) {
        if (!prevProps.visible && this.props.visible) {
            this.props.stepChangeCallback(this.props.stepSlug, this.getActiveInputMethod()?.canProceed() ?? false);
        }
    }

    private getFieldsAsType() {
        return this.fields as unknown as TWSInputMethodFields;
    }
}

